/** What is outstanding across the tenant. */

import type { ReactElement } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { request } from '@/lib/api';
import type { TenantCounts } from '@/lib/schemas';
import { Card, ErrorState, Loading } from '@/components/ui';

const TILES: { key: keyof TenantCounts; label: string; to: string }[] = [
  { key: 'active_cases', label: 'Active cases', to: '/cases' },
  { key: 'running_runs', label: 'Runs in progress', to: '/cases' },
  // **"At the review gate", not "awaiting review".** The count is `run.status =
  // 'awaiting_review'`, which a run holds both while it is blocked on a reviewer *and*
  // after the decision, while synthesis finishes. Labelling all of it as awaiting review
  // promised an action that does not exist: the status transitions only on the advance
  // task, and nothing an operator can click moves it. `/review` names these runs now, so
  // the tile lands somewhere that accounts for what it counted.
  { key: 'runs_awaiting_review', label: 'Runs at the review gate', to: '/review' },
  { key: 'pending_proposals', label: 'Merges to decide', to: '/review' },
  { key: 'unconfirmed_hypotheses', label: 'Hypotheses to review', to: '/review' },
  // **Two things that were previously silent.** A failed run was visible only to somebody
  // who already opened the case, and an unhealthy source only to somebody who opened the
  // Sources page — so a collection that stopped working stayed unnoticed until a report
  // came back thin. Both link to where the problem is fixed.
  { key: 'failed_runs', label: 'Runs that failed', to: '/cases' },
  { key: 'unhealthy_plugins', label: 'Sources unhealthy', to: '/plugins' },
];

/** The dashboard view. */
export function DashboardPage(): ReactElement {
  const counts = useQuery({
    queryKey: ['dashboard'],
    queryFn: () => request<TenantCounts>('/dashboard'),
  });

  if (counts.isLoading) return <Loading />;
  if (counts.error) return <ErrorState error={counts.error} />;

  return (
    <div className="space-y-6">
      <h1 className="text-lg font-semibold">Dashboard</h1>
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">
        {TILES.map((tile) => (
          <Link key={tile.key} to={tile.to} className="rounded-lg border border-line bg-raised p-4">
            <p className="text-2xl font-semibold tabular-nums">{counts.data?.[tile.key] ?? 0}</p>
            <p className="text-xs text-muted">{tile.label}</p>
          </Link>
        ))}
      </div>
      <Card title="What this platform will not do">
        <ul className="list-disc space-y-1 pl-5 text-sm text-muted">
          <li>Merge two people without a reviewer deciding.</li>
          <li>Publish a generated claim that cites nothing.</li>
          <li>Export a risk hypothesis nobody has confirmed.</li>
        </ul>
      </Card>
    </div>
  );
}
